import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Search } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import './styles/PlatformConfiguration.css';
import { createUser } from '../../services/userService';
import { getOrganizations } from '../../services/departmentService';
import { toast } from 'react-toastify';

interface Props {
  nextStep: () => void;
  previousStep: () => void;
}

interface SystemUser {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'User';
  initial: string;
  avatarColor: string;
}

interface Organization {
  id: string;
  tenantCode: string;
  title: string;
  description: string | null;
  parentOrganizationId: string | null;
  organizationTypeId?: string;
  email?: string;
  phone?: string;
  website?: string;
  createdAt?: string;
}

interface InviteUserFormValues {
  fullName: string;
  email: string;
  systemRole: SystemUser['role'] | '';
  portalType: string;
  orgUnit: string;
  reportingManager: string;
}

const CURRENT_USERS: SystemUser[] = [
  {
    id: '1',
    name: 'Abdullah Al-Jabri',
    email: 'nys.abdullah@gmail.com',
    role: 'Admin',
    initial: 'A',
    avatarColor: '#e0f2fe',
  },
  {
    id: '2',
    name: 'gocera8257',
    email: 'gocera8257@gfanet.com',
    role: 'User',
    initial: 'G',
    avatarColor: '#d1fae5',
  },
  {
    id: '3',
    name: 'seyos28194',
    email: 'seyos28194@gfanet.com',
    role: 'User',
    initial: 'S',
    avatarColor: '#fce7f3',
  },
  {
    id: '4',
    name: 'lotind2685',
    email: 'lotind2685@kidbox.com',
    role: 'Admin',
    initial: 'L',
    avatarColor: '#e0e7ff',
  },
  {
    id: '5',
    name: 'h053a',
    email: 'h053a@outlookjohsons.com',
    role: 'Admin',
    initial: 'H',
    avatarColor: '#fef3c7',
  },
];

const SUBSIDIARIES = ['Nova', 'BAA', 'BCCA'];
const SYSTEM_ROLES: SystemUser['role'][] = ['Admin', 'User'];

const inviteUserValidationSchema = Yup.object({
  fullName: Yup.string().trim().required('Full name is required'),
  email: Yup.string().trim().email('Enter a valid email address').required('Email is required'),
  systemRole: Yup.mixed<SystemUser['role'] | ''>().oneOf(['', ...SYSTEM_ROLES]),
  portalType: Yup.string().oneOf(['', 'ACTIVE', 'PASSIVE']),
  orgUnit: Yup.string(),
  reportingManager: Yup.string(),
});

const generateTempPassword = () => {
  // Simple random temp password generator
  return Math.random().toString(36).slice(-12) + 'A1!';
};

const UserSetup = ({ nextStep, previousStep }: Props) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [canAccessSubsidiaries, setCanAccessSubsidiaries] = useState(true);
  const [selectedSubsidiaries, setSelectedSubsidiaries] = useState<string[]>(['Nova', 'BAA', 'BCCA']);
  const [skipForNow, setSkipForNow] = useState(false);

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [hasAddedUser, setHasAddedUser] = useState(false);

  const filteredUsers = CURRENT_USERS.filter(
    (u) => u.name.toLowerCase().includes(searchQuery.toLowerCase()) || u.email.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const toggleSubsidiary = (name: string) => {
    setSelectedSubsidiaries((prev) => (prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]));
  };

  const fullNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const formik = useFormik<InviteUserFormValues>({
    initialValues: {
      fullName: '',
      email: '',
      systemRole: '',
      portalType: '',
      orgUnit: '',
      reportingManager: '',
    },
    validationSchema: inviteUserValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          username: values.fullName,
          firstName: values.fullName,
          email: values.email,
          password: generateTempPassword(),
          organizationId: values.orgUnit,
          status: 'ACTIVE', // adjust to match your backend's actual enum value
          mustChangePassword: true, // force reset since it's a system-generated temp password
          roleNames: [values.systemRole],
          type: values.portalType,
        };

        const response = await createUser(payload);
        console.log('User created:', response);

        resetForm();
        setSelectedSubsidiaries([]);
        setCanAccessSubsidiaries(false);
        setHasAddedUser(true);
        toast.success('User created successfully');
      } catch (err: any) {
        console.error(err);
        const backendMessage = err?.response?.data?.message || err?.message;
        toast.error(backendMessage || 'Failed to create user.');
      }
    },
  });

  const handleNextClick = async () => {
    if (hasAddedUser || skipForNow) {
      nextStep();
      return;
    }

    const errors = await formik.validateForm();

    if (errors.fullName || errors.email) {
      formik.setTouched({ ...formik.touched, fullName: true, email: true });

      if (errors.fullName) {
        fullNameRef.current?.focus();
      } else if (errors.email) {
        emailRef.current?.focus();
      }
      return;
    }

    nextStep();
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        const data = await getOrganizations();
        setOrganizations(data);
      } catch (err) {
        setError('Failed to load organizations');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  return (
    <div className="user-setup-card">
      {/* Header */}
      <div className="user-setup-header">
        <h2 className="user-setup-title">User Setup</h2>
        <p className="user-setup-subtitle">Add users or skip to add them later</p>
      </div>

      {/* Current System Users */}
      <div className="user-section">
        <div className="user-section-header">
          <Search size={16} className="user-section-icon" />
          <h3 className="user-section-title">Current System Users</h3>
        </div>

        <div className="user-list">
          {filteredUsers.map((user) => (
            <div key={user.id} className="user-list-item">
              <div className="user-list-left">
                <div className="user-avatar" style={{ backgroundColor: user.avatarColor }}>
                  <span className="user-avatar-initial">{user.initial}</span>
                </div>
                <div className="user-list-info">
                  <span className="user-list-name">{user.name}</span>
                  <span className="user-list-email">{user.email}</span>
                </div>
              </div>
              <span className={`user-role-badge ${user.role === 'Admin' ? 'user-role-admin' : 'user-role-user'}`}>{user.role}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Invite New Users */}
      <div className="user-section">
        <h3 className="user-section-title invite-title">Invite New Users</h3>
        <p className="user-section-desc">Add users to invite now, or skip and invite them later from the users module</p>

        <form onSubmit={formik.handleSubmit} noValidate>
          {/* Full Name + Email */}
          <div className="user-form-row mb-4">
            <div className="user-form-field">
              <label className="user-form-label" htmlFor="fullName">
                Full Name<span className="required">*</span>
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                className="user-form-input"
                ref={fullNameRef}
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Full name"
              />
              {formik.touched.fullName && formik.errors.fullName && (
                <span className="user-form-error" style={{ color: '#dc2626', fontSize: '12px' }}>
                  {formik.errors.fullName}
                </span>
              )}
            </div>
            <div className="user-form-field">
              <label className="user-form-label" htmlFor="email">
                Email Address<span className="required">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className="user-form-input"
                ref={emailRef}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="user@company.com"
              />
              {formik.touched.email && formik.errors.email && (
                <span className="user-form-error" style={{ color: '#dc2626', fontSize: '12px' }}>
                  {formik.errors.email}
                </span>
              )}
            </div>
          </div>

          {/* System Role + Portal Type */}
          <div className="user-form-row mb-4">
            <div className="user-form-field">
              <label className="user-form-label" htmlFor="systemRole">
                System Role
              </label>
              <select
                id="systemRole"
                name="systemRole"
                className="user-form-input"
                value={formik.values.systemRole}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="" disabled>
                  Select role
                </option>
                {SYSTEM_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
              {formik.touched.systemRole && formik.errors.systemRole && <span className="user-form-error">{formik.errors.systemRole}</span>}
            </div>
            <div className="user-form-field">
              <label className="user-form-label" htmlFor="portalType">
                Portal Type
              </label>
              <select
                id="portalType"
                name="portalType"
                className="user-form-input"
                value={formik.values.portalType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="" disabled>
                  Select portal type
                </option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="PASSIVE">PASSIVE</option>
              </select>
              {formik.touched.portalType && formik.errors.portalType && <span className="user-form-error">{formik.errors.portalType}</span>}
            </div>
          </div>

          {/* Org Unit */}
          <div className="user-form-field user-form-field-full mb-4">
            <label className="user-form-label" htmlFor="orgUnit">
              Org Unit (optional)
            </label>
            <select
              id="orgUnit"
              name="orgUnit"
              className="user-form-input"
              value={formik.values.orgUnit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={loading}
            >
              <option value="">{loading ? 'Loading organizations...' : 'Select Org Unit'}</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.title}
                </option>
              ))}
            </select>
            {error && <span className="user-form-error">{error}</span>}
          </div>

          {/* Can access subsidiaries */}
          <div className="user-subsidiaries-block mb-4">
            <div className="user-toggle-row">
              <div className="user-toggle-text">
                <span className="user-toggle-label">Can access subsidiaries</span>
                <span className="user-toggle-hint">Allow this user to switch between subsidiary companies</span>
              </div>
              <button
                type="button"
                className={`user-toggle ${canAccessSubsidiaries ? 'user-toggle-on' : ''}`}
                onClick={() => setCanAccessSubsidiaries((v) => !v)}
                aria-pressed={canAccessSubsidiaries}
              >
                <span className="user-toggle-knob" />
              </button>
            </div>

            {canAccessSubsidiaries && (
              <div className="user-subsidiary-list">
                {SUBSIDIARIES.map((sub) => {
                  const checked = selectedSubsidiaries.includes(sub);
                  return (
                    <label key={sub} className="user-subsidiary-item">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSubsidiary(sub)}
                        className="user-subsidiary-checkbox"
                      />
                      <span className="user-subsidiary-name">{sub}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Reporting Manager */}
          <div className="user-form-field user-form-field-full mb-4">
            <label className="user-form-label" htmlFor="reportingManager">
              Reporting Manager (optional)
            </label>
            <input
              id="reportingManager"
              name="reportingManager"
              type="text"
              className="user-form-input"
              value={formik.values.reportingManager}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Set reporting manager"
            />
          </div>

          {/* Add User / Cancel */}
          <div className="user-form-actions">
            <button type="submit" className="user-add-btn" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Adding...' : '+ Add User'}
            </button>
            <button type="button" className="user-cancel-btn" onClick={() => formik.resetForm()}>
              Cancel
            </button>
          </div>
        </form>

        {/* Skip for now */}
        <label className="user-skip-row">
          <input type="checkbox" checked={skipForNow} onChange={(e) => setSkipForNow(e.target.checked)} className="user-skip-checkbox" />
          <span className="user-skip-text">Skip for now — I'll add users later from the users module</span>
        </label>
      </div>

      {/* Footer */}
      <div className="user-setup-footer">
        <button type="button" onClick={previousStep} className="user-back-btn">
          <ArrowLeft size={16} />
          Back
        </button>
        <button type="button" onClick={handleNextClick} className="user-next-btn">
          Next
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default UserSetup;
